import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    IconButton,
    Tooltip
} from '@mui/material';
import { createLicense, fetchLicenses, fetchDealers, updateDealer } from '../api/licenseService';
import EditIcon from '@mui/icons-material/Edit';
import EditDealerDialog from './EditDealerDialog';

const licenseTypes = ['Fixed', 'Subscription'];
const products = ['SuperPos', 'Lieferando'];

function CreateLicenseDialog({ onClose, onCreated, parentLicense }) {
    const [newLicense, setNewLicense] = useState({
        tillId: parentLicense?.tillId || '',
        owner: '',
        product: parentLicense ? 'Lieferando' : 'SuperPos',
        licenseType: 'Fixed',
        isActive: true,
        validUntil: '',
        ownerDetails: {
            company: '',
            vatId: '',
            name: '',
            firstName: '',
            email: '',
            phoneNumber: '',
            street: '',
            houseNameOrNumber: '',
            city: '',
            postCode: '',
            country: ''
        },
        apiKeys: null,
        dealer: null
    });

    const [dealers, setDealers] = useState([]);
    const [editDealer, setEditDealer] = useState(null);

    useEffect(() => {
        if (newLicense.product === 'Lieferando') {
            setNewLicense((prev) => ({
                ...prev,
                apiKeys: {
                    menuApiKey: '',
                    orderingApiKey: '',
                    restaurantId: ''
                }
            }));
        } else {
            setNewLicense((prev) => ({
                ...prev,
                apiKeys: null
            }));
        }
    }, [newLicense.product]);

    useEffect(() => {
        const loadDealers = async () => {
            const apiKey = localStorage.getItem('apiKey');
            const res = await fetchDealers(apiKey);
            setDealers(res || []);
        };
        loadDealers();
    }, []);

    const handleChange = (field) => (e) => {
        setNewLicense({ ...newLicense, [field]: e.target.value });
    };

    const handleCheckboxChange = (field) => (e) => {
        setNewLicense({ ...newLicense, [field]: e.target.checked });
    };

    const handleOwnerDetailChange = (field) => (e) => {
        setNewLicense({
            ...newLicense,
            ownerDetails: {
                ...newLicense.ownerDetails,
                [field]: e.target.value
            }
        });
    };

    const handleApiKeyChange = (field) => (e) => {
        setNewLicense({
            ...newLicense,
            apiKeys: {
                ...newLicense.apiKeys,
                [field]: e.target.value
            }
        });
    };

    const handleSave = async () => {
        try {
            if (parentLicense) {
                const all = await fetchLicenses();
                const exists = all.some(
                    (l) =>
                        l.tillId === parentLicense.tillId &&
                        l.product === newLicense.product
                );
                if (exists) {
                    alert('Dieses Feature wurde bereits hinzugefügt.');
                    return;
                }
            }

            const dealerPassword = localStorage.getItem('password');
            await createLicense(newLicense, dealerPassword);
            onCreated();
            onClose();
        } catch (err) {
            console.error(err);
            alert('Fehler beim Erstellen der Lizenz.');
        }
    };

    return (
        <>
            <Dialog open onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Neue Lizenz erstellen</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Inhaber"
                        fullWidth
                        margin="normal"
                        value={newLicense.owner}
                        onChange={handleChange('owner')}
                    />
                    <TextField
                        select
                        label="Produkt"
                        fullWidth
                        margin="normal"
                        value={newLicense.product}
                        onChange={handleChange('product')}
                    >
                        {products.map((p) => (
                            <MenuItem key={p} value={p}>
                                {p}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Lizenztyp"
                        fullWidth
                        margin="normal"
                        value={newLicense.licenseType}
                        onChange={handleChange('licenseType')}
                    >
                        {licenseTypes.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </TextField>

                    {!parentLicense && (
                        <>
                            <TextField
                                label="Kassen Id"
                                fullWidth
                                margin="normal"
                                value={newLicense.tillId}
                                onChange={handleChange('tillId')}
                            />

                            <FormControl fullWidth margin="normal">
                                <InputLabel>Händler</InputLabel>
                                <Select
                                    value={newLicense.distributor?.id || ''}
                                    onChange={(e) => {
                                        const selected = dealers.find(
                                            (d) => d.id === e.target.value
                                        );
                                        setNewLicense({ ...newLicense, distributor: selected });
                                    }}
                                >
                                    {dealers.map((d) => (
                                        <MenuItem key={d.id} value={d.id}>
                                            {d.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Tooltip title="Händler bearbeiten">
                                <IconButton
                                    onClick={() => setEditDealer(newLicense.distributor)}
                                    disabled={!newLicense.distributor}
                                    size="small"
                                    sx={{ mt: 1 }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={newLicense.isActive}
                                onChange={handleCheckboxChange('isActive')}
                            />
                        }
                        label="Aktiv"
                        sx={{ mt: 1 }}
                    />

                    <TextField
                        label="Gültig bis"
                        fullWidth
                        margin="normal"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={newLicense.validUntil}
                        onChange={handleChange('validUntil')}
                    />

                    {!parentLicense && newLicense.ownerDetails && (
                        <>
                            <h4>Inhaberdaten</h4>
                            <TextField label="Firma" fullWidth margin="normal" value={newLicense.ownerDetails.company} onChange={handleOwnerDetailChange('company')} />
                            <TextField label="USt-ID" fullWidth margin="normal" value={newLicense.ownerDetails.vatId} onChange={handleOwnerDetailChange('vatId')} />
                            <TextField label="Nachname" fullWidth margin="normal" value={newLicense.ownerDetails.name} onChange={handleOwnerDetailChange('name')} />
                            <TextField label="Vorname" fullWidth margin="normal" value={newLicense.ownerDetails.firstName} onChange={handleOwnerDetailChange('firstName')} />
                            <TextField label="E-Mail" fullWidth margin="normal" value={newLicense.ownerDetails.email} onChange={handleOwnerDetailChange('email')} />
                            <TextField label="Telefonnummer" fullWidth margin="normal" value={newLicense.ownerDetails.phoneNumber} onChange={handleOwnerDetailChange('phoneNumber')} />
                            <TextField label="Straße" fullWidth margin="normal" value={newLicense.ownerDetails.street} onChange={handleOwnerDetailChange('street')} />
                            <TextField label="Hausnummer" fullWidth margin="normal" value={newLicense.ownerDetails.houseNameOrNumber} onChange={handleOwnerDetailChange('houseNameOrNumber')} />
                            <TextField label="Stadt" fullWidth margin="normal" value={newLicense.ownerDetails.city} onChange={handleOwnerDetailChange('city')} />
                            <TextField label="PLZ" fullWidth margin="normal" value={newLicense.ownerDetails.postCode} onChange={handleOwnerDetailChange('postCode')} />
                            <TextField label="Land" fullWidth margin="normal" value={newLicense.ownerDetails.country} onChange={handleOwnerDetailChange('country')} />
                        </>
                    )}

                    {newLicense.product === 'Lieferando' && newLicense.apiKeys && (
                        <>
                            <h4>API-Schlüssel</h4>
                            <TextField label="Menu API Key" fullWidth margin="normal" value={newLicense.apiKeys.menuApiKey || ''} onChange={handleApiKeyChange('menuApiKey')} />
                            <TextField label="Ordering API Key" fullWidth margin="normal" value={newLicense.apiKeys.orderingApiKey || ''} onChange={handleApiKeyChange('orderingApiKey')} />
                            <TextField label="Restaurant-ID" fullWidth margin="normal" value={newLicense.apiKeys.restaurantId || ''} onChange={handleApiKeyChange('restaurantId')} />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave}>Erstellen</Button>
                </DialogActions>
            </Dialog>

            {editDealer && (
                <EditDealerDialog
                    dealer={editDealer}
                    onClose={() => setEditDealer(null)}
                    onSave={async (updated) => {
                        const apiKey = localStorage.getItem('apiKey');
                        await updateDealer(updated, apiKey);
                        setEditDealer(null);
                        setDealers((prev) =>
                            prev.map((d) => (d.id === updated.id ? updated : d))
                        );
                        if (newLicense.distributor?.id === updated.id) {
                            setNewLicense({ ...newLicense, distributor: updated });
                        }
                    }}
                />
            )}
        </>
    );
}

export default CreateLicenseDialog;
